import { useEffect } from 'react'
import { APIProvider, Map, Marker, useMap, useMapsLibrary } from '@vis.gl/react-google-maps'
import type { ClubMemberMap, ClubMemberPoint } from '../../types'

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''

/**
 * Public club map showing where the club's members are located, aggregated by
 * ZIP centroid (counts only — no names or exact addresses). Falls back to a
 * tidy area summary when no Google Maps key is configured.
 */
export function MemberLocationMap({ data }: { data: ClubMemberMap }) {
  if (!data || data.points.length === 0) return null

  return (
    <section className="border border-border rounded-lg p-6 bg-surface">
      <h2 className="font-heading font-semibold text-text mb-1">Where Our Members Are</h2>
      <p className="text-sm text-text-muted mb-4">
        {data.totalMapped} of {data.totalMembers} member{data.totalMembers !== 1 ? 's' : ''} shown by general area.
      </p>

      {GOOGLE_MAPS_API_KEY ? (
        <div className="h-80 rounded-md overflow-hidden border border-border">
          <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
            <Map
              defaultZoom={6}
              defaultCenter={centroid(data.points)}
              gestureHandling="cooperative"
              disableDefaultUI={false}
              className="w-full h-full"
            >
              <MemberMarkers points={data.points} />
            </Map>
          </APIProvider>
        </div>
      ) : (
        <AreaSummary points={data.points} />
      )}
    </section>
  )
}

function MemberMarkers({ points }: { points: ClubMemberPoint[] }) {
  const map = useMap()
  const coreLib = useMapsLibrary('core')

  useEffect(() => {
    if (!map || !coreLib || points.length === 0) return
    const bounds = new coreLib.LatLngBounds()
    points.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }))
    map.fitBounds(bounds, 60)
  }, [map, coreLib, points])

  return (
    <>
      {points.map((p, i) => (
        <Marker
          key={`${p.lat},${p.lng},${i}`}
          position={{ lat: p.lat, lng: p.lng }}
          icon={countPinIcon(p.count)}
          title={[p.city, p.state].filter(Boolean).join(', ')}
        />
      ))}
    </>
  )
}

/** No-Google-Maps fallback: member counts grouped by state. */
function AreaSummary({ points }: { points: ClubMemberPoint[] }) {
  const byState: Record<string, number> = {}
  for (const p of points) {
    const key = p.state || 'Other'
    byState[key] = (byState[key] || 0) + p.count
  }
  const states = Object.entries(byState).sort((a, b) => b[1] - a[1])

  return (
    <div className="flex flex-wrap gap-2">
      {states.map(([state, count]) => (
        <span
          key={state}
          className="px-3 py-1.5 rounded-full bg-surface-alt border border-border text-sm text-text"
        >
          <span className="font-semibold">{state}</span>
          <span className="text-text-muted"> · {count}</span>
        </span>
      ))}
    </div>
  )
}

function centroid(points: ClubMemberPoint[]): { lat: number; lng: number } {
  const lat = points.reduce((s, p) => s + p.lat, 0) / points.length
  const lng = points.reduce((s, p) => s + p.lng, 0) / points.length
  return { lat, lng }
}

/** A crimson circular pin with the member count, as an SVG data URI (no Map ID needed). */
function countPinIcon(count: number): string {
  const digits = String(count).length
  const r = 11 + digits * 2
  const size = (r + 3) * 2
  const fontSize = count >= 100 ? 11 : 13
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}">` +
    `<circle cx="${size / 2}" cy="${size / 2}" r="${r}" fill="#d11149" stroke="#ffffff" stroke-width="2"/>` +
    `<text x="${size / 2}" y="${size / 2}" dy="0.35em" text-anchor="middle" ` +
    `font-family="Arial, sans-serif" font-size="${fontSize}" font-weight="700" fill="#ffffff">${count}</text>` +
    `</svg>`
  return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg)
}
